import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Space, Dropdown, message, Modal, Tag, Tooltip, Progress, Button, Alert, Image, Skeleton, Empty } from 'antd';
import { MoreOutlined, EditOutlined, CheckCircleOutlined, StopOutlined, CloseCircleOutlined, ReloadOutlined, FileTextOutlined, EyeOutlined, ExclamationCircleOutlined, CheckOutlined, CameraOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserNurse, faCheckCircle, faClock, faBan, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { usePageHeader } from '../../../layouts/AdminLayout';
import OnDemandHCWService from '../../../services/OnDemandHCWService';
import OnDemandPhotoService from '../../../services/OnDemandPhotoService';
import OnDemandStorageUnitService from '../../../services/OnDemandStorageUnitService';
import {
  PageContainer,
  TopBar,
  SearchInput,
  ActionButton,
  StyledTable,
  StyledModal,
  FormSection,
  EmptyState,
  StatsRow,
  StatCard,
  ApprovalBadge,
} from './styled';

const approvalStatusLabels = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  suspended: 'Suspended',
};

const approvalStatusIcons = {
  pending: faClock,
  approved: faCheckCircle,
  rejected: faBan,
  suspended: faExclamationTriangle,
};

// Documents required for HCW approval (must mirror backend list).
const REQUIRED_DOC_TYPES = ['government_id', 'license', 'background_check_consent'];
const DOC_TYPE_LABELS = {
  government_id: 'Government ID',
  license: 'License',
  background_check_consent: 'Background Check',
  insurance: 'Insurance',
};

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'];
const fileExtOf = (name) =>
  String(name || '').split('?')[0].split('.').pop().toLowerCase();
const isImageFile = (name) => IMAGE_EXTS.includes(fileExtOf(name));

function getDocsReadiness(hcw) {
  const docs = Array.isArray(hcw?.documents) ? hcw.documents : [];
  const approvedTypes = new Set(
    docs.filter((d) => d.status === 'approved').map((d) => d.type)
  );
  const submittedTypes = new Set(docs.map((d) => d.type));
  const missing = REQUIRED_DOC_TYPES.filter((t) => !approvedTypes.has(t));
  return {
    approvedCount: REQUIRED_DOC_TYPES.filter((t) => approvedTypes.has(t)).length,
    submittedCount: REQUIRED_DOC_TYPES.filter((t) => submittedTypes.has(t)).length,
    requiredCount: REQUIRED_DOC_TYPES.length,
    missing,
  };
}

const HCWsPage = () => {
  const { setPageHeader } = usePageHeader();

  const [hcws, setHCWs] = useState([]);
  const [storageUnits, setStorageUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingHCW, setEditingHCW] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [selectedHCW, setSelectedHCW] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalReason, setApprovalReason] = useState('');
  const [docsModalVisible, setDocsModalVisible] = useState(false);
  const [docsModalHCW, setDocsModalHCW] = useState(null);
  const [docUrlLoadingId, setDocUrlLoadingId] = useState(null);
  const [docReviewLoadingId, setDocReviewLoadingId] = useState(null);
  const [docThumbUrls, setDocThumbUrls] = useState({}); // docId → signed image url
  const [docPreview, setDocPreview] = useState({ open: false, url: null, fileName: '', title: '' });
  const [rejectDocModal, setRejectDocModal] = useState({ open: false, doc: null });
  const [rejectDocReason, setRejectDocReason] = useState('');
  const [rejectDocSaving, setRejectDocSaving] = useState(false);
  const [forceApproveModalVisible, setForceApproveModalVisible] = useState(false);
  const [forceApproveHCW, setForceApproveHCW] = useState(null);
  const [forceApproveAction, setForceApproveAction] = useState(null);
  const [forceApproveReason, setForceApproveReason] = useState('');
  const [forceApproveSaving, setForceApproveSaving] = useState(false);
  // Kit photo history modal
  const [kitHistoryModal, setKitHistoryModal] = useState({ open: false, hcw: null });
  const [kitHistoryLoading, setKitHistoryLoading] = useState(false);
  const [kitHistoryItems, setKitHistoryItems] = useState([]);
  const [kitHistoryUrls, setKitHistoryUrls] = useState({}); // assignmentId → signed url
  const [form] = Form.useForm();

  useEffect(() => {
    setPageHeader({ title: 'Healthcare Workers', showBack: false });
  }, [setPageHeader]);

  const fetchHCWs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OnDemandHCWService.getHCWs({
        search: searchText,
        approvalStatus: statusFilter,
      });
      if (response?.success) {
        setHCWs(response.data);
      }
    } catch (error) {
      console.error('Error fetching HCWs:', error);
      message.error('Failed to load healthcare workers');
    } finally {
      setLoading(false);
    }
  }, [searchText, statusFilter]);

  const fetchStorageUnits = useCallback(async () => {
    try {
      const response = await OnDemandStorageUnitService.getStorageUnits({ isActive: true });
      if (response?.success) {
        setStorageUnits(response.data);
      }
    } catch (error) {
      console.error('Error fetching storage units:', error);
    }
  }, []);

  useEffect(() => {
    fetchHCWs();
    fetchStorageUnits();
  }, [fetchHCWs, fetchStorageUnits]);

  const handleCreate = () => {
    setEditingHCW(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingHCW(record);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      phone: record.phone,
      approvedStorageUnits: record.approvedStorageUnits?.map(s => s._id) || [],
      street: record.shippingAddress?.street,
      city: record.shippingAddress?.city,
      state: record.shippingAddress?.state,
      zipCode: record.shippingAddress?.zipCode,
      notes: record.notes,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        approvedStorageUnits: values.approvedStorageUnits || [],
        shippingAddress: {
          street: values.street,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode,
        },
        notes: values.notes,
      };

      if (editingHCW) {
        const response = await OnDemandHCWService.updateHCW(editingHCW._id, payload);
        if (response?.success) {
          message.success('Healthcare worker updated');
        }
      } else {
        const response = await OnDemandHCWService.createHCW(payload);
        if (response?.success) {
          message.success('Healthcare worker created');
        }
      }

      setModalVisible(false);
      fetchHCWs();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (!error.errorFields) {
        message.error('Failed to save healthcare worker');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await OnDemandHCWService.deleteHCW(id);
      if (response?.success) {
        message.success('Healthcare worker deactivated');
        fetchHCWs();
      }
    } catch (error) {
      message.error('Failed to deactivate healthcare worker');
    }
  };

  const openApprovalModal = (hcw, action) => {
    setSelectedHCW(hcw);
    setApprovalAction(action);
    setApprovalReason('');
    setApprovalModalVisible(true);
  };

  const handleApproval = async () => {
    try {
      const response = await OnDemandHCWService.updateApproval(selectedHCW._id, {
        action: approvalAction,
        reason: approvalReason,
      });
      if (response?.success) {
        message.success(response.message);
        setApprovalModalVisible(false);
        fetchHCWs();
      }
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update approval status');
      }
    }
  };

  const openDocsModal = (hcw) => {
    setDocsModalHCW(hcw);
    setDocsModalVisible(true);
    // Resolve signed URLs for image documents so we can show inline
    // thumbnails. Tolerates individual failures.
    setDocThumbUrls({});
    const imageDocs = (hcw?.documents || []).filter(
      (d) => d.fileUrl && isImageFile(d.fileName)
    );
    if (imageDocs.length) {
      (async () => {
        const map = {};
        await Promise.all(
          imageDocs.map(async (d) => {
            try {
              const r = await OnDemandHCWService.getDocumentUrl(hcw._id, d._id);
              if (r?.success && r.data?.url) map[d._id] = r.data.url;
            } catch (_) {
              /* tolerate single-thumbnail failures */
            }
          })
        );
        setDocThumbUrls(map);
      })();
    }
  };

  // Cross-event kit-photo audit: every kit photo this HCW has ever uploaded.
  const openKitHistory = async (hcw) => {
    setKitHistoryModal({ open: true, hcw });
    setKitHistoryItems([]);
    setKitHistoryUrls({});
    setKitHistoryLoading(true);
    try {
      const resp = await OnDemandHCWService.getKitPhotoHistory(hcw._id);
      if (resp?.success) {
        const items = resp.data || [];
        setKitHistoryItems(items);
        // Fan-out: resolve signed URLs for thumbnails in parallel.
        const urlMap = {};
        await Promise.all(items.map(async (it) => {
          try {
            const r = await OnDemandPhotoService.getKitPhotoUrl(it.eventId, it.assignmentId);
            if (r?.success && r.data?.url) urlMap[it.assignmentId] = r.data.url;
          } catch (_) { /* tolerate single-photo failures */ }
        }));
        setKitHistoryUrls(urlMap);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load kit photo history');
    } finally {
      setKitHistoryLoading(false);
    }
  };

  const handleViewDoc = async (hcwId, doc) => {
    // Re-use an already-resolved thumbnail URL when we have one to avoid a
    // second round-trip; otherwise fetch a fresh signed URL.
    const cached = docThumbUrls[doc._id];
    if (cached) {
      setDocPreview({
        open: true,
        url: cached,
        fileName: doc.fileName || '',
        title: DOC_TYPE_LABELS[doc.type] || doc.type,
      });
      return;
    }
    setDocUrlLoadingId(doc._id);
    try {
      const resp = await OnDemandHCWService.getDocumentUrl(hcwId, doc._id);
      if (resp?.success && resp.data?.url) {
        setDocPreview({
          open: true,
          url: resp.data.url,
          fileName: doc.fileName || '',
          title: DOC_TYPE_LABELS[doc.type] || doc.type,
        });
      } else {
        message.error(resp?.message || 'Could not generate document URL');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Could not open document');
    } finally {
      setDocUrlLoadingId(null);
    }
  };

  // Apply a reviewed document back into BOTH the open modal and the table
  // list so the status badge and the row's Documents column update instantly,
  // without waiting for the refetch round-trip.
  const patchReviewedDoc = (hcwId, updated) => {
    if (!hcwId || !updated?._id) return;
    const applyToHcw = (hcw) => {
      if (!hcw || String(hcw._id) !== String(hcwId)) return hcw;
      const nextDocs = (hcw.documents || []).map((d) =>
        String(d._id) === String(updated._id)
          ? {
              ...d,
              status: updated.status,
              rejectionReason: updated.rejectionReason,
              reviewedAt: updated.reviewedAt,
              reviewedBy: updated.reviewedBy,
            }
          : d
      );
      return { ...hcw, documents: nextDocs };
    };
    setHCWs((prev) => (Array.isArray(prev) ? prev.map(applyToHcw) : prev));
    setDocsModalHCW((prev) =>
      prev && String(prev._id) === String(hcwId) ? applyToHcw(prev) : prev
    );
  };

  const handleApproveDoc = async (hcwId, doc) => {
    setDocReviewLoadingId(doc._id);
    try {
      const resp = await OnDemandHCWService.updateDocumentStatus(hcwId, doc._id, {
        status: 'approved',
      });
      if (resp?.success) {
        message.success('Document approved');
        patchReviewedDoc(hcwId, resp.data?.document);
        fetchHCWs();
      } else {
        message.error(resp?.message || 'Failed to approve document');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to approve document');
    } finally {
      setDocReviewLoadingId(null);
    }
  };

  const openRejectDocModal = (doc) => {
    setRejectDocReason('');
    setRejectDocModal({ open: true, doc });
  };

  const handleSubmitRejectDoc = async () => {
    const doc = rejectDocModal.doc;
    if (!doc || !docsModalHCW) return;
    if (!rejectDocReason.trim()) {
      message.warning('Please describe what the HCW needs to fix.');
      return;
    }
    setRejectDocSaving(true);
    try {
      const resp = await OnDemandHCWService.updateDocumentStatus(
        docsModalHCW._id,
        doc._id,
        { status: 'rejected', rejectionReason: rejectDocReason.trim() }
      );
      if (resp?.success) {
        message.success('Document rejected — HCW will be notified');
        patchReviewedDoc(docsModalHCW._id, resp.data?.document);
        fetchHCWs();
        setRejectDocModal({ open: false, doc: null });
        setRejectDocReason('');
      } else {
        message.error(resp?.message || 'Failed to reject document');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to reject document');
    } finally {
      setRejectDocSaving(false);
    }
  };

  // Approve / reactivate flow with admin override on missing docs.
  const tryApprove = async (hcw, action) => {
    const readiness = getDocsReadiness(hcw);
    const incomplete = readiness.missing.length > 0;
    if (incomplete) {
      setForceApproveHCW(hcw);
      setForceApproveAction(action);
      setForceApproveReason('');
      setForceApproveModalVisible(true);
      return;
    }
    try {
      const resp = await OnDemandHCWService.updateApproval(hcw._id, { action });
      if (resp?.success) {
        message.success(resp.message);
        fetchHCWs();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleForceApprove = async () => {
    setForceApproveSaving(true);
    try {
      const resp = await OnDemandHCWService.updateApproval(forceApproveHCW._id, {
        action: forceApproveAction,
        reason: forceApproveReason || undefined,
        force: true,
      });
      if (resp?.success) {
        message.success(resp.message || 'Healthcare worker approved (override)');
        setForceApproveModalVisible(false);
        fetchHCWs();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to approve');
    } finally {
      setForceApproveSaving(false);
    }
  };

  const pendingCount = hcws.filter(h => h.approvalStatus === 'pending').length;
  const approvedCount = hcws.filter(h => h.approvalStatus === 'approved').length;
  const suspendedCount = hcws.filter(h => h.approvalStatus === 'suspended').length;

  const getApprovalActions = (record) => {
    const actions = [];
    switch (record.approvalStatus) {
      case 'pending':
        actions.push({ label: 'Approve', action: 'approve', type: 'primary' });
        actions.push({ label: 'Reject', action: 'reject', type: 'danger' });
        break;
      case 'approved':
        actions.push({ label: 'Suspend', action: 'suspend', type: 'danger' });
        break;
      case 'rejected':
        actions.push({ label: 'Approve', action: 'approve', type: 'primary' });
        break;
      case 'suspended':
        actions.push({ label: 'Reactivate', action: 'reactivate', type: 'primary' });
        break;
      default:
        break;
    }
    return actions;
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space size={6}>
          <strong>{record.firstName} {record.lastName}</strong>
          {record.portalAccess?.enabledAt && (
            <Tooltip title="Access given from User Management — signs in with their web portal login">
              <Tag color="blue" style={{ marginRight: 0 }}>Portal user</Tag>
            </Tooltip>
          )}
        </Space>
      ),
      sorter: (a, b) => a.lastName.localeCompare(b.lastName),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 13 }}>{record.email}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.phone}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      width: 130,
      render: (status) => (
        <ApprovalBadge $status={status}>
          <FontAwesomeIcon icon={approvalStatusIcons[status] || faClock} />
          {approvalStatusLabels[status] || status}
        </ApprovalBadge>
      ),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Approved', value: 'approved' },
        { text: 'Rejected', value: 'rejected' },
        { text: 'Suspended', value: 'suspended' },
      ],
      onFilter: (value, record) => record.approvalStatus === value,
    },
    {
      title: 'Documents',
      key: 'documents',
      width: 160,
      align: 'center',
      render: (_, record) => {
        const r = getDocsReadiness(record);
        const docs = Array.isArray(record.documents) ? record.documents : [];
        const byType = {};
        docs.forEach((d) => {
          const cur = byType[d.type];
          // Prefer an approved document over any other status so the bar and
          // tooltip match the "X/3 approved" count when duplicate records exist
          // for the same type (e.g. a stale pending copy alongside an approved one).
          if (!cur || (cur.status !== 'approved' && d.status === 'approved')) {
            byType[d.type] = d;
          }
        });
        const approvedTypes = new Set(
          docs.filter((d) => d.status === 'approved').map((d) => d.type)
        );
        const segColor = {
          approved: '#52c41a',
          pending: '#faad14',
          rejected: '#ff4d4f',
          expired: '#bfbfbf',
        };
        // A submission awaiting an admin decision (uploaded file or in-app
        // consent) for a type that isn't already approved.
        const needsReview = docs.some(
          (d) =>
            d.status === 'pending' &&
            (d.fileUrl || d.consentAcknowledgedAt) &&
            !approvedTypes.has(d.type)
        );
        const tooltip = (
          <div style={{ fontSize: 12 }}>
            {REQUIRED_DOC_TYPES.map((t) => {
              const d = byType[t];
              const label = DOC_TYPE_LABELS[t] || t;
              const state = d
                ? (d.status === 'approved'
                    ? 'Approved'
                    : d.status === 'rejected'
                      ? 'Rejected'
                      : 'Submitted · pending review')
                : 'Not submitted';
              return (
                <div key={t}>
                  {label}: {state}
                </div>
              );
            })}
          </div>
        );
        return (
          <Tooltip title={tooltip}>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 5,
              }}
              onClick={() => openDocsModal(record)}
            >
              <div style={{ display: 'flex', gap: 4, width: 96 }}>
                {REQUIRED_DOC_TYPES.map((t) => {
                  const d = byType[t];
                  const bg = d ? segColor[d.status] || '#faad14' : '#e8e8e8';
                  return (
                    <span
                      key={t}
                      style={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        background: bg,
                      }}
                    />
                  );
                })}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#595959' }}>
                {r.approvedCount}/{r.requiredCount} approved
              </div>
              {needsReview ? (
                <Tag
                  color="warning"
                  style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 6px' }}
                >
                  Needs review
                </Tag>
              ) : r.submittedCount === 0 ? (
                <Tag
                  style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 6px', color: '#bfbfbf' }}
                >
                  None submitted
                </Tag>
              ) : null}
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => getDocsReadiness(a).approvedCount - getDocsReadiness(b).approvedCount,
    },
    {
      title: 'Events',
      key: 'events',
      width: 70,
      align: 'center',
      render: (_, record) => record.stats?.totalEventsCompleted || 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const approvalIconMap = {
          approve: <CheckCircleOutlined />,
          reject: <CloseCircleOutlined />,
          suspend: <StopOutlined />,
          reactivate: <ReloadOutlined />,
        };
        const items = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleEdit(record),
          },
        ];
        getApprovalActions(record).forEach(({ label, action, type }) => {
          items.push({
            key: action,
            icon: approvalIconMap[action] || null,
            label,
            danger: type === 'danger',
            onClick: () => {
              if (action === 'approve' || action === 'reactivate') {
                tryApprove(record, action);
              } else {
                openApprovalModal(record, action);
              }
            },
          });
        });
        items.push({ type: 'divider' });
        items.push({
          key: 'documents',
          icon: <FileTextOutlined />,
          label: 'View Documents',
          onClick: () => openDocsModal(record),
        });
        items.push({
          key: 'kit-history',
          icon: <CameraOutlined />,
          label: 'Kit Photo History',
          onClick: () => openKitHistory(record),
        });
        if (record.isActive) {
          items.push({ type: 'divider' });
          items.push({
            key: 'deactivate',
            icon: <StopOutlined />,
            label: 'Deactivate',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Deactivate this healthcare worker?',
                onOk: () => handleDelete(record._id),
              });
            },
          });
        }
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <ActionButton
              type="text"
              icon={<MoreOutlined style={{ fontSize: 20 }} />}
              style={{ border: 'none', boxShadow: 'none' }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <div className="header-animate-in">
        <StatsRow>
          <StatCard $bgColor="#e6f7ff" $iconColor="#1890ff">
            <div className="stat-icon"><FontAwesomeIcon icon={faUserNurse} /></div>
            <div className="stat-info">
              <div className="stat-value">{hcws.length}</div>
              <div className="stat-label">Total HCWs</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#fff7e6" $iconColor="#faad14">
            <div className="stat-icon"><FontAwesomeIcon icon={faClock} /></div>
            <div className="stat-info">
              <div className="stat-value">{pendingCount}</div>
              <div className="stat-label">Pending Approval</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#f6ffed" $iconColor="#52c41a">
            <div className="stat-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
            <div className="stat-info">
              <div className="stat-value">{approvedCount}</div>
              <div className="stat-label">Approved</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#fff2f0" $iconColor="#ff4d4f">
            <div className="stat-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
            <div className="stat-info">
              <div className="stat-value">{suspendedCount}</div>
              <div className="stat-label">Suspended</div>
            </div>
          </StatCard>
        </StatsRow>

        <TopBar>
          <Space>
            <SearchInput
              placeholder="Search by name, email, or phone..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <Select
              placeholder="All Statuses"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160, height: 40 }}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="approved">Approved</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
              <Select.Option value="suspended">Suspended</Select.Option>
            </Select>
          </Space>
          <ActionButton type="primary" onClick={handleCreate}>
            + Add Healthcare Worker
          </ActionButton>
        </TopBar>
      </div>

      <div className="table-animate-in">
        {hcws.length === 0 && !loading ? (
          <EmptyState>
            <div className="icon"><FontAwesomeIcon icon={faUserNurse} /></div>
            <div className="title">No Healthcare Workers Yet</div>
            <div className="description">Add healthcare workers to start managing your on-demand team</div>
            <ActionButton ghost onClick={handleCreate}>+ Add Healthcare Worker</ActionButton>
          </EmptyState>
        ) : (
          <StyledTable
            columns={columns}
            dataSource={hcws}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 900, y: 'calc(100vh - 380px)' }}
            sticky
            pagination={{
              defaultPageSize: 20,
              pageSizeOptions: ['10', '20', '50', '100'],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} healthcare workers`,
            }}
          />
        )}
      </div>

      {/* Create/Edit Modal */}
      <StyledModal
        title={editingHCW ? 'Edit Healthcare Worker' : 'Add Healthcare Worker'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={640}
        okText={editingHCW ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <FormSection>
            <div className="section-title">Personal Information</div>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'Required' }]} style={{ flex: 1 }}>
                <Input placeholder="Jane" />
              </Form.Item>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Required' }]} style={{ flex: 1 }}>
                <Input placeholder="Smith" />
              </Form.Item>
            </Space>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]} style={{ flex: 1 }}>
                <Input placeholder="jane@example.com" />
              </Form.Item>
              <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Required' }]} style={{ flex: 1 }}>
                <Input placeholder="(555) 123-4567" />
              </Form.Item>
            </Space>
          </FormSection>

          <FormSection>
            <div className="section-title">Assignments</div>
            <Form.Item name="approvedStorageUnits" label="Approved Storage Units">
              <Select mode="multiple" placeholder="Select storage units HCW can access">
                {storageUnits.map(s => (
                  <Select.Option key={s._id} value={s._id}>{s.name} ({s.code})</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </FormSection>

          <FormSection>
            <div className="section-title">Shipping Address</div>
            <Form.Item name="street" label="Street">
              <Input placeholder="123 Main St" />
            </Form.Item>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="city" label="City" style={{ flex: 2 }}>
                <Input placeholder="Dallas" />
              </Form.Item>
              <Form.Item name="state" label="State" style={{ flex: 1 }}>
                <Input placeholder="TX" />
              </Form.Item>
              <Form.Item name="zipCode" label="Zip" style={{ flex: 1 }}>
                <Input placeholder="75201" />
              </Form.Item>
            </Space>
          </FormSection>

          <FormSection>
            <div className="section-title">Notes</div>
            <Form.Item name="notes">
              <Input.TextArea rows={3} placeholder="Internal notes about this healthcare worker..." />
            </Form.Item>
          </FormSection>
        </Form>
      </StyledModal>

      {/* Approval Action Modal (for reject/suspend that require reason) */}
      <Modal
        title={`${approvalAction === 'reject' ? 'Reject' : 'Suspend'} Healthcare Worker`}
        open={approvalModalVisible}
        onOk={handleApproval}
        onCancel={() => setApprovalModalVisible(false)}
        okText={approvalAction === 'reject' ? 'Reject' : 'Suspend'}
        okButtonProps={{ danger: true }}
      >
        <p>
          {approvalAction === 'reject'
            ? `Are you sure you want to reject ${selectedHCW?.firstName} ${selectedHCW?.lastName}?`
            : `Are you sure you want to suspend ${selectedHCW?.firstName} ${selectedHCW?.lastName}?`
          }
        </p>
        <Input.TextArea
          rows={3}
          placeholder="Reason (required)..."
          value={approvalReason}
          onChange={(e) => setApprovalReason(e.target.value)}
        />
      </Modal>

      {/* Documents Viewer Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#e6f9fb',
                color: '#1292A4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FileTextOutlined style={{ fontSize: 18 }} />
            </div>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {docsModalHCW?.firstName || ''} {docsModalHCW?.lastName || ''}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
                {docsModalHCW?.email || 'Document review'}
              </div>
            </div>
          </div>
        }
        open={docsModalVisible}
        onCancel={() => setDocsModalVisible(false)}
        footer={<Button onClick={() => setDocsModalVisible(false)}>Close</Button>}
        width={720}
        bodyStyle={{ padding: '20px 24px', background: '#fafbfc' }}
      >
        {docsModalHCW && (() => {
          const r = getDocsReadiness(docsModalHCW);
          const docs = docsModalHCW.documents || [];
          const items = REQUIRED_DOC_TYPES.map((type) => ({
            type,
            doc: docs.find((d) => d.type === type),
          })).concat(
            docs
              .filter((d) => !REQUIRED_DOC_TYPES.includes(d.type))
              .map((d) => ({ type: d.type, doc: d }))
          );
          const statusMeta = {
            approved: { color: '#52c41a', bg: '#f6ffed', label: 'Approved' },
            pending: { color: '#faad14', bg: '#fffbe6', label: 'Pending review' },
            rejected: { color: '#ff4d4f', bg: '#fff1f0', label: 'Rejected' },
            expired: { color: '#8c8c8c', bg: '#f5f5f5', label: 'Expired' },
          };
          return (
            <>
              {/* Summary header */}
              <div
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Required documents</span>
                  <Space size={16}>
                    <span style={{ fontSize: 13, color: '#595959' }}>
                      <strong style={{ color: '#1292A4' }}>{r.submittedCount}</strong>
                      /{r.requiredCount} submitted
                    </span>
                    <span style={{ fontSize: 13, color: '#595959' }}>
                      <strong style={{ color: r.approvedCount === r.requiredCount ? '#52c41a' : '#faad14' }}>
                        {r.approvedCount}
                      </strong>
                      /{r.requiredCount} approved
                    </span>
                  </Space>
                </div>
                <Progress
                  percent={Math.round((r.approvedCount / r.requiredCount) * 100)}
                  strokeColor={r.approvedCount === r.requiredCount ? '#52c41a' : '#1292A4'}
                  size="small"
                />
                {r.missing.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12, borderRadius: 8 }}
                    message={`Missing or unapproved: ${r.missing.map((t) => DOC_TYPE_LABELS[t] || t).join(', ')}`}
                  />
                )}
              </div>

              {/* Document cards */}
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                {items.map(({ type, doc }) => {
                  const meta = doc ? statusMeta[doc.status] || statusMeta.pending : null;
                  const hasFile = !!doc?.fileUrl;
                  const thumbUrl = doc ? docThumbUrls[doc._id] : null;
                  // Anything the HCW has actually submitted can be reviewed —
                  // including the background-check consent which has no file.
                  const canReview = !!doc && (hasFile || !!doc.consentAcknowledgedAt);
                  return (
                    <div
                      key={`${type}-${doc?._id || 'missing'}`}
                      style={{
                        position: 'relative',
                        background: '#fff',
                        borderRadius: 12,
                        padding: 14,
                        border: '1px solid #f0f0f0',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      {/* Quick-view eye, top-right of the card */}
                      {hasFile && (
                        <Tooltip title="View document">
                          <Button
                            type="text"
                            size="small"
                            shape="circle"
                            icon={<EyeOutlined style={{ fontSize: 17, color: '#1292A4' }} />}
                            loading={docUrlLoadingId === doc._id}
                            onClick={() => handleViewDoc(docsModalHCW._id, doc)}
                            style={{ position: 'absolute', top: 8, right: 8 }}
                          />
                        </Tooltip>
                      )}

                      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                        {/* Thumbnail — click to preview, with an eye overlay */}
                        <div
                          onClick={hasFile ? () => handleViewDoc(docsModalHCW._id, doc) : undefined}
                          style={{
                            position: 'relative',
                            width: 72,
                            height: 72,
                            borderRadius: 12,
                            background: thumbUrl
                              ? '#000'
                              : doc
                                ? '#e6f9fb'
                                : '#f5f5f5',
                            color: doc ? '#1292A4' : '#bfbfbf',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            overflow: 'hidden',
                            cursor: hasFile ? 'pointer' : 'default',
                          }}
                        >
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt={DOC_TYPE_LABELS[type] || type}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <FileTextOutlined style={{ fontSize: 26 }} />
                          )}
                          {hasFile && thumbUrl && (
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(0,0,0,0.30)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <EyeOutlined style={{ fontSize: 22, color: '#fff' }} />
                            </div>
                          )}
                        </div>

                        <div style={{ flex: 1, minWidth: 0, paddingRight: 28 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>
                              {DOC_TYPE_LABELS[type] || type}
                            </span>
                            {doc && meta ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: meta.color,
                                  background: meta.bg,
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                }}
                              >
                                {meta.label}
                              </span>
                            ) : (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: '#bfbfbf',
                                  background: '#fafafa',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                }}
                              >
                                Not submitted
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.5 }}>
                            {doc ? (
                              <>
                                <div>
                                  {doc.fileName ||
                                    (doc.consentAcknowledgedAt
                                      ? 'Consent acknowledged in-app'
                                      : '—')}
                                </div>
                                <div>
                                  {doc.uploadedAt && (
                                    <>Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</>
                                  )}
                                  {doc.expirationDate && (
                                    <> · Expires {new Date(doc.expirationDate).toLocaleDateString()}</>
                                  )}
                                </div>
                                {doc.rejectionReason && (
                                  <div style={{ color: '#ff4d4f', marginTop: 4 }}>
                                    Rejected: {doc.rejectionReason}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span>Not yet uploaded by the HCW</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Review actions — full width, flush right: Reject · Approve */}
                      {canReview && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 8,
                          }}
                        >
                          {doc.status !== 'rejected' && (
                            <Button
                              size="small"
                              danger
                              icon={<CloseCircleOutlined />}
                              onClick={() => openRejectDocModal(doc)}
                            >
                              Reject
                            </Button>
                          )}
                          {doc.status !== 'approved' && (
                            <Button
                              size="small"
                              icon={<CheckOutlined />}
                              loading={docReviewLoadingId === doc._id}
                              onClick={() => handleApproveDoc(docsModalHCW._id, doc)}
                              style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      )}
                      {doc && !hasFile && !doc.consentAcknowledgedAt && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tag color="default" style={{ marginRight: 0 }}>
                            No file
                          </Tag>
                        </div>
                      )}

                    </div>
                  );
                })}
              </Space>
            </>
          );
        })()}
      </Modal>

      {/* In-app document preview (image / PDF) */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <EyeOutlined style={{ color: '#1292A4', fontSize: 18 }} />
            <span style={{ fontWeight: 600 }}>{docPreview.title || 'Document'}</span>
          </div>
        }
        open={docPreview.open}
        onCancel={() => setDocPreview({ open: false, url: null, fileName: '', title: '' })}
        footer={[
          <Button
            key="ext"
            icon={<EyeOutlined />}
            onClick={() =>
              docPreview.url &&
              window.open(docPreview.url, '_blank', 'noopener,noreferrer')
            }
          >
            Open in new tab
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => setDocPreview({ open: false, url: null, fileName: '', title: '' })}
            style={{ background: '#1292A4', borderColor: '#1292A4' }}
          >
            Close
          </Button>,
        ]}
        width={900}
        centered
        bodyStyle={{ padding: 0, background: '#1f1f1f' }}
      >
        {docPreview.url && (() => {
          const ext = fileExtOf(docPreview.fileName || docPreview.url);
          const isImage = IMAGE_EXTS.includes(ext);
          const isPdf = ext === 'pdf';
          if (isImage) {
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                  maxHeight: '75vh',
                  overflow: 'auto',
                  padding: 16,
                }}
              >
                <img
                  src={docPreview.url}
                  alt={docPreview.title}
                  style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 6 }}
                />
              </div>
            );
          }
          if (isPdf) {
            return (
              <iframe
                src={docPreview.url}
                title={docPreview.title}
                style={{ width: '100%', height: '75vh', border: 'none', display: 'block' }}
              />
            );
          }
          return (
            <div style={{ padding: 40, textAlign: 'center', color: '#d9d9d9' }}>
              <FileTextOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>
                {docPreview.fileName || 'This file'}
              </div>
              <div style={{ fontSize: 13, color: '#8c8c8c' }}>
                This file type can&apos;t be previewed here. Use &ldquo;Open in new
                tab&rdquo; to view it.
              </div>
            </div>
          );
        })()}
      </Modal>


      <Modal
        title={
          <span>
            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            Reject document
          </span>
        }
        open={rejectDocModal.open}
        onCancel={() => {
          setRejectDocModal({ open: false, doc: null });
          setRejectDocReason('');
        }}
        onOk={handleSubmitRejectDoc}
        okText="Reject & notify HCW"
        okButtonProps={{ danger: true, loading: rejectDocSaving }}
        confirmLoading={rejectDocSaving}
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>
          The HCW will see this message in their Documents screen and can re-upload
          a corrected file. Be specific about what needs to change.
        </p>
        <Input.TextArea
          rows={4}
          value={rejectDocReason}
          onChange={(e) => setRejectDocReason(e.target.value)}
          placeholder="e.g. License photo is blurry — please re-upload a clearer image showing the expiration date."
          maxLength={500}
          showCount
          autoFocus
        />
      </Modal>

      {/* Force-approve confirmation modal (when docs/bg check incomplete) */}
      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            {forceApproveAction === 'reactivate' ? 'Reactivate' : 'Approve'} without all requirements?
          </span>
        }
        open={forceApproveModalVisible}
        onOk={handleForceApprove}
        confirmLoading={forceApproveSaving}
        onCancel={() => setForceApproveModalVisible(false)}
        okText={`Yes, ${forceApproveAction === 'reactivate' ? 'reactivate' : 'approve'} anyway`}
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        {forceApproveHCW && (() => {
          const r = getDocsReadiness(forceApproveHCW);
          return (
            <>
              <p>
                <strong>{forceApproveHCW.firstName} {forceApproveHCW.lastName}</strong> is missing
                requirements. Approving will record an audit override on this worker.
              </p>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Outstanding requirements"
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {r.missing.length > 0 && (
                      <li>
                        Documents not approved:{' '}
                        {r.missing.map((t) => DOC_TYPE_LABELS[t] || t).join(', ')}
                      </li>
                    )}
                  </ul>
                }
              />
              <Form.Item label="Override reason (optional)" style={{ marginBottom: 0 }}>
                <Input.TextArea
                  rows={3}
                  placeholder="Why are you approving without all requirements?"
                  value={forceApproveReason}
                  onChange={(e) => setForceApproveReason(e.target.value)}
                />
              </Form.Item>
            </>
          );
        })()}
      </Modal>

      {/* Cross-event kit-photo history audit gallery. */}
      <Modal
        title={
          kitHistoryModal.hcw
            ? `Kit Photo History — ${kitHistoryModal.hcw.firstName} ${kitHistoryModal.hcw.lastName}`
            : 'Kit Photo History'
        }
        open={kitHistoryModal.open}
        onCancel={() => setKitHistoryModal({ open: false, hcw: null })}
        footer={<Button onClick={() => setKitHistoryModal({ open: false, hcw: null })}>Close</Button>}
        width={760}
        destroyOnClose
      >
        {kitHistoryLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : kitHistoryItems.length === 0 ? (
          <Empty description="No kit photos uploaded yet" />
        ) : (
          <Image.PreviewGroup>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              {kitHistoryItems.map((item) => {
                const url = kitHistoryUrls[item.assignmentId];
                return (
                  <div
                    key={item.assignmentId}
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      padding: 8,
                      background: '#fafbfc',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        background: '#000',
                        borderRadius: 6,
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {url ? (
                        <Image
                          src={url}
                          alt={item.eventName}
                          width="100%"
                          height={140}
                          style={{ objectFit: 'cover' }}
                          preview={{ mask: 'Zoom' }}
                        />
                      ) : (
                        <span style={{ color: '#8c8c8c', fontSize: 12 }}>Unavailable</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#262626' }} title={item.eventName}>
                      {item.eventName}
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                      {item.uploadedAt
                        ? new Date(item.uploadedAt).toLocaleString()
                        : (item.eventStartDateTime ? new Date(item.eventStartDateTime).toLocaleDateString() : '')}
                    </div>
                  </div>
                );
              })}
            </div>
          </Image.PreviewGroup>
        )}
      </Modal>
    </PageContainer>
  );
};

export default HCWsPage;
